/**
 * WHERE A DIFFERENT PLAN DOES BETTER — one line per other goal, under the cards (owner, 22 Oct 2026: "why are you
 * putting the best cashflow card with the best legacy card … each section has a specific purpose").
 *
 * A goal shows its own leader, and a second card only for the plan choice rule's near-tie (planChoiceRule.ts:
 * nearly the same estate while locking less away or with a better cashflow). Any other goal led by a different plan
 * is pointed to here, as support at home has done since 20 Oct: which goal, that plan's figure against the plan on
 * screen, what it costs the family, and a link to its tab. Shared by the village, downsizing, granny flat and
 * cashflow & Age Pension.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';

export type OtherGoal = 'legacy' | 'cashflow' | 'pension';

export interface OtherGoalLeader {
    goal: OtherGoal;
    title: string;
    legacy: number;
    cashflow: number;
    pension: number;
}

const HEAD: Record<OtherGoal, string> = {
    cashflow: 'If more to live on each year matters most',
    pension: 'If the Age Pension matters most',
    legacy: 'If leaving the most to the family matters most',
};
const NAME: Record<OtherGoal, string> = { cashflow: 'Most left over each year', pension: 'The highest Age Pension', legacy: 'Leaving the most to the family' };

export interface OtherGoalLeadersProps {
    others: OtherGoalLeader[];
    /** The plan on the left (the goal's leader), for the comparison. */
    here: { legacy: number; cashflow: number; pension: number } | null;
    /** The benchmark's estate, and its name ("staying put", "doing nothing", "changing nothing"). */
    baseLegacy?: number | null;
    baseWord: string;
    onFocus: (goal: OtherGoal) => void;
    testId?: string;
}

const money = (n: number) => formatCurrency(Math.round(n));
const cf = (n: number) => (n >= 0 ? `${money(n)} a year to spare` : `${money(Math.abs(n))} a year short`);

const OtherGoalLeaders = ({ others, here, baseLegacy = null, baseWord, onFocus, testId = 'other-goal-leaders' }: OtherGoalLeadersProps) => {
    if (!others.length) return null;
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-testid={testId}>
            <h3 className="text-[17px] font-bold text-slate-900">{others.length === 1 ? HEAD[others[0]!.goal] : 'Where a different plan does better'}</h3>
            <p className="mt-1 text-[13px] text-slate-500">{others.length === 1 ? 'The plan above is the best for every other goal.' : 'The plan above is the best for the goal chosen.'}</p>
            <ul className="mt-2 space-y-3 text-[14px] leading-relaxed text-slate-700">
                {others.map((o) => {
                    const fact = o.goal === 'cashflow'
                        ? <>{cf(o.cashflow)}{here ? <>, against {cf(here.cashflow)} with the plan above</> : null}</>
                        : o.goal === 'pension'
                            ? <>{money(o.pension)} a year of Age Pension{here ? <>, against {money(here.pension)}</> : null}</>
                            : <>{money(o.legacy)} left to the family{here ? <>, against {money(here.legacy)}</> : null}</>;
                    const dl = baseLegacy != null ? o.legacy - baseLegacy : 0;
                    return (
                        <li key={o.goal}>
                            {others.length > 1 && <strong className="text-slate-900">{NAME[o.goal]}: </strong>}
                            <strong className="text-slate-900">{o.title}</strong> gives {fact}.
                            {baseLegacy != null && Math.abs(dl) >= 1 && o.goal !== 'legacy' && <> But it leaves the family {money(Math.abs(dl))} {dl >= 0 ? 'more' : 'less'} than {baseWord}.</>}{' '}
                            <button type="button" onClick={() => onFocus(o.goal)} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">See that plan</button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default OtherGoalLeaders;
